import { EventType } from "@/analytics/analytics-events";
import { ReportConfigurationDialog } from "@/components/ui/report-configuration-dialog";
import { SnapshotConfigurationDialog } from "@/components/ui/snapshot-configuration-dialog";
import { CaptureTree } from "@/components/ui/tree/capture-tree/capture-tree";
import {
  CaptureTreeContextProvider,
  useCaptureTreeContext,
} from "@/components/ui/tree/capture-tree/capture-tree-context";
import { useNodeCheckbox } from "@/components/ui/tree/capture-tree/capture-tree-hooks";
import { useCaptureTreeSelectionStore } from "@/components/ui/tree/capture-tree/capture-tree-selection-store";
import { ProjectFilter } from "@/components/ui/tree/capture-tree/project-filter";
import { useTreeContext } from "@/components/ui/tree/tree-context";
import { TreeHeightWrapper } from "@/components/ui/tree/tree-height-wrapper";
import { useToolBrowserHistory } from "@/features/navigation-history/use-navigation-history";
import { useCurrentArea, useCurrentScene } from "@/modes/mode-data-context";
import { selectActiveCadId, setActiveCad } from "@/store/cad/cad-slice";
import { hideLightweightElements } from "@/store/lightweight-elements-slice";
import { changeMode } from "@/store/mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { deactivateTool, ToolName } from "@/store/ui/ui-slice";
import { TreeData } from "@/utils/tree-generation/tree-data";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import {
  Alert,
  CloseIcon,
  FaroButton,
  FaroCheckbox,
  FaroIconButton,
  FaroText,
  grey,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/foundation";
import { IElementType, IElementTypeHint } from "@faro-lotv/ielement-types";
import {
  hideIElements,
  selectAllAreaDatasets,
  selectProjectName,
  setAreaDataSets,
} from "@faro-lotv/project-source";
import { DataSetAreaInfo } from "@faro-lotv/service-wires";
import { Box, Stack } from "@mui/system";
import { isEqual } from "es-toolkit";
import { useCallback, useEffect, useState, type JSX } from "react";
import { NodeApi, TreeApi } from "react-arborist";
import {
  selectElementsToHide,
  selectIsMainElementHidden,
} from "./create-report-selectors";
import {
  CreateReportTreeNode,
  useNodeCheckboxState,
} from "./create-report-tree-node";
import {
  computeInitialSheetSelection,
  initPdfReportSheetSelection,
} from "./pdf-report-slice";

/**
 * @returns The panel of the "Create report" tool that replaces the default project overview panel
 * when the tool is active
 */
export function CreateReportPanel(): JSX.Element {
  const projectName = useAppSelector(selectProjectName);
  const { tree } = useTreeContext();
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();

  // Collect all the iElements that should not be visible in the app when the tool is active
  const hiddenIElementIds = useAppSelector(selectElementsToHide, isEqual);

  const { area } = useCurrentArea();
  const { main } = useCurrentScene();

  // Check if the currently displayed element should be changed since it
  // is not visible anymore in the current tool
  const isMainElementHidden = useAppSelector(
    selectIsMainElementHidden(main, hiddenIElementIds),
  );

  // The active cad in the store and the list of datasets must be removed while in the create
  // report tool, to make them invisible to the user. To do so, we store the initial values in
  // a state so that we can change the store in the use effect and restore the correct
  // initial values when deactivating the tool
  const activeCadId = useAppSelector(selectActiveCadId);
  const [cad] = useState(activeCadId);

  const areaDatasets = useAppSelector(selectAllAreaDatasets);
  const [datasetsInfo] = useState(areaDatasets);

  const isSnapshotTool =
    useAppSelector(selectActiveTool) === ToolName.createSnapshot;
  const [isSnapshotToolState] = useState(isSnapshotTool);

  const [supportedDatasets] = useState(() => {
    const supportedDatasets: Record<GUID, DataSetAreaInfo[]> = {};
    for (const [areaId, dataSetAreaInfo] of Object.entries(areaDatasets)) {
      // The snapshot tool does not support capture-tree scan datasets, so
      // exclude them from the area to also hide their waypoints in the 3D scene.
      supportedDatasets[areaId] = isSnapshotToolState
        ? []
        : (dataSetAreaInfo?.filter(
            (dataSetArea) =>
              dataSetArea.elementType === IElementType.section &&
              dataSetArea.elementTypeHint === IElementTypeHint.dataSetCapture,
          ) ?? []);
    }
    return supportedDatasets;
  });

  // On tool activation, hide all elements that are not supported by this tool (datasets, cads, etc...).
  // On tool deactivation, re-show all the hidden elements.
  const clearSelection = useCaptureTreeSelectionStore(
    (state) => state.clearSelection,
  );
  useEffect(() => {
    dispatch(setActiveCad(undefined));

    dispatch(setAreaDataSets(supportedDatasets));
    dispatch(hideIElements(hiddenIElementIds));
    dispatch(hideLightweightElements(hiddenIElementIds));

    // Clear the selection when activating the tool
    clearSelection();

    // When the tool is deactivated, restore the full iElements store
    return () => {
      clearSelection();
      dispatch(hideIElements([]));
      dispatch(hideLightweightElements([]));
    };
  }, [supportedDatasets, clearSelection, dispatch, hiddenIElementIds]);

  // When the area and the active element change, if the element is hidden by the report tool,
  // we should change the active element
  useEffect(() => {
    if (isMainElementHidden) {
      dispatch(setActiveElement(area?.id));
      dispatch(changeMode("sheet"));
    }
  }, [area?.id, isMainElementHidden, dispatch]);

  // Re-enable the active CAD element once the tool is deactivated
  useEffect(
    () => () => {
      dispatch(setActiveCad(cad));

      const datasets: Record<GUID, DataSetAreaInfo[]> = {};
      for (const [key, info] of Object.entries(datasetsInfo)) {
        if (info) {
          datasets[key] = info;
        }
      }
      dispatch(setAreaDataSets(datasets));
    },
    [cad, dispatch, datasetsInfo],
  );

  const [treeRef, setTreeRef] = useState<
    TreeApi<TreeData> | null | undefined
  >();

  const rootNode = treeRef?.root;

  const isPDFReport =
    useAppSelector(selectActiveTool) === ToolName.createPDFReport;

  // Initialize the pdf report state, when activating the pdf report tool
  useNonExhaustiveEffect(() => {
    if (!isPDFReport) return;
    dispatch(
      initPdfReportSheetSelection(computeInitialSheetSelection(getState())),
    );
  }, [isPDFReport]);

  const deactivateCreateReportTool = useCallback(() => {
    if (isPDFReport) {
      Analytics.track(EventType.closePDFReportTool);
    } else {
      Analytics.track(EventType.closeSnapshotTool);
    }
    dispatch(deactivateTool());
    dispatch(hideIElements([]));
    dispatch(hideLightweightElements([]));
  }, [dispatch, isPDFReport]);
  useToolBrowserHistory(
    isPDFReport ? ToolName.createPDFReport : ToolName.createSnapshot,
    deactivateCreateReportTool,
  );

  return (
    <Stack
      aria-label="create-report-panel"
      sx={{ gap: 1, width: "100%", flex: 1, overflow: "auto" }}
    >
      <Stack
        direction="row"
        sx={{ width: "100%", px: 1.5, pt: 1, justifyContent: "space-between" }}
      >
        <Stack direction="column">
          <FaroText variant="labelS" textTransform="uppercase">
            <TranslateVar name="project name">{projectName}</TranslateVar>
          </FaroText>
          <FaroText variant="heading14">
            {isPDFReport ? "Create report" : "Create snapshot"}
          </FaroText>
        </Stack>
        <FaroIconButton onClick={deactivateCreateReportTool}>
          <CloseIcon />
        </FaroIconButton>
      </Stack>
      <FaroText variant="bodyS" sx={{ color: grey[600], px: 1.5 }}>
        {isPDFReport
          ? "Select data to include in the report. Click on a location name to navigate to it."
          : "Select data to include in the snapshot. Click on a location name to navigate to it."}
      </FaroText>
      <Box component="div" sx={{ px: 1.5, width: "100%" }}>
        <ProjectFilter />
      </Box>
      <CaptureTreeContextProvider>
        <Stack sx={{ height: "100%", gap: 0 }}>
          {rootNode && tree.length > 0 && <RootNode rootNode={rootNode} />}
          {tree.length === 0 && (
            <Box component="div" sx={{ height: "100%", width: "100%" }} />
          )}
          {tree.length > 0 && (
            <TreeHeightWrapper>
              <CaptureTree tree={tree} ref={setTreeRef}>
                {CreateReportTreeNode}
              </CaptureTree>
            </TreeHeightWrapper>
          )}
          {isPDFReport && (
            <Alert variant="neutral" sx={{ mx: 1.5, mt: 1 }} title="">
              Only one sheet per area may be included in the PDF report. Make
              sure the correct one is displayed.
            </Alert>
          )}
          <CreateReportButtons />
        </Stack>
      </CaptureTreeContextProvider>
    </Stack>
  );
}

type RootNodeProps = {
  rootNode: NodeApi<TreeData>;
};

function RootNode({ rootNode }: RootNodeProps): JSX.Element {
  const { selectedLeaves, visibleLeaves } = useCaptureTreeContext();
  const onNodeCheckbox = useNodeCheckbox();
  const { isChecked, isIndeterminate } = useNodeCheckboxState(rootNode);

  return (
    <Stack
      direction="row"
      sx={{
        gap: 0,
        width: "100%",
        pl: 2,
        pr: 0,
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <FaroText variant="bodyM">
        <TranslateVar name="selected-leaves">
          {selectedLeaves.length}
        </TranslateVar>{" "}
        of <TranslateVar name="visible-leaves">{visibleLeaves}</TranslateVar>
      </FaroText>
      <FaroCheckbox
        checked={isChecked}
        indeterminate={isIndeterminate}
        onClick={() => onNodeCheckbox(rootNode)}
        controlSx={{ mr: 1 }}
      />
    </Stack>
  );
}

function CreateReportButtons(): JSX.Element {
  const dispatch = useAppDispatch();

  const { selectedLeaves } = useCaptureTreeContext();

  const [showDialog, setShowDialog] = useState(false);

  const deactivateCreateReportTool = useCallback(() => {
    dispatch(deactivateTool());
    dispatch(hideIElements([]));
    dispatch(hideLightweightElements([]));
  }, [dispatch]);

  const isPDFReport =
    useAppSelector(selectActiveTool) === ToolName.createPDFReport;

  return (
    <Stack direction="row" sx={{ width: "100%", p: 2, gap: 2 }}>
      <FaroButton
        aria-label="cancel report"
        variant="ghost"
        sx={{ flex: 1 }}
        size="s"
        onClick={deactivateCreateReportTool}
      >
        Cancel
      </FaroButton>
      <FaroButton
        aria-label="configure report"
        sx={{ flex: 1 }}
        size="s"
        disabled={!selectedLeaves.length}
        onClick={() => setShowDialog(true)}
      >
        {isPDFReport ? "Create report..." : "Create snapshot..."}
      </FaroButton>
      {isPDFReport && (
        <ReportConfigurationDialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
        />
      )}
      {!isPDFReport && (
        <SnapshotConfigurationDialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
        />
      )}
    </Stack>
  );
}
